import React from 'react';
import classnames from 'classnames';
import { Breakpoint, DialogTitle, DialogContent, DialogActions, Dialog, Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Button } from 'framework/ui/mui-builder';

import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import useModalWindow from './modalHelper';

interface ModalProps {
  buttons?: React.ReactNode;
  title?: Nullable<string>;
  titleAdornment?: React.ReactNode;
  children: React.ReactNode | React.ReactNode[];
  isOpen: boolean;
  onToggle: () => void;
  hasCancelButton?: boolean;
  modalSize?: Breakpoint | false;
  keyboard?: boolean;
  backdrop?: boolean | 'static';
  className?: string;
  hasCloseIcon?: boolean;
  paperStyles?: Dictionary<any>;
  paperSx?: SxProps<Theme>;
}

const Modal = ({
  children,
  title,
  titleAdornment,
  buttons,
  isOpen,
  onToggle,
  hasCancelButton = true,
  modalSize = 'smallMobile',
  backdrop = true,
  keyboard = true,
  className,
  hasCloseIcon = false,
  paperStyles = {},
  paperSx,
}: ModalProps) => {
  const { onModalContentClick, isFocusEnable } = useModalWindow();

  const onClose = (event: object, reason?: string) => {
    if (reason === 'backdropClick' && (!backdrop || backdrop === 'static')) {
      return;
    }

    if (reason === 'escapeKeyDown' && !keyboard) {
      return;
    }

    onToggle();
  };

  return (
    <Dialog
      fullWidth
      slotProps={{
        paper: {
          style: { overflowY: 'visible', ...paperStyles },
          sx: [
            theme => ({
              p: '16px',
              backgroundColor: 'background.paper',
              borderRadius: '4px',
              boxShadow: `0 8px 12px 6px ${theme.palette.common.shadowMedium}, 0 4px 4px ${theme.palette.common.shadowStrong}`,
              [theme.breakpoints.down('sm')]: {
                m: '13px',
                width: 'calc(100% - 26px)',
              },
              [theme.breakpoints.down('md')]: { maxWidth: 'calc(100% - 26px)' },
              [theme.breakpoints.up('md')]: { p: '20px 24px', m: '22px' },
            }),
            ...(Array.isArray(paperSx) ? paperSx : [paperSx]),
          ],
        },
      }}
      className={classnames('modal-window', className)}
      open={isOpen}
      onClose={onClose}
      scroll="body"
      maxWidth={modalSize}
      disableEnforceFocus={isFocusEnable}
    >
      {(title || hasCloseIcon) && (
        <DialogTitle
          component="h3"
          sx={{
            minHeight: '59px',
            typography: 'h3',
            color: 'text.primary',
            mt: '5px',
            p: '0 45px 23px 0',
          }}
        >
          <Box display="flex" alignItems="center" gap={1} pr={4}>
            {title}
            {titleAdornment}
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={theme => ({
              position: 'absolute',
              top: '19px',
              right: '7px',
              color: 'text.secondary',
              [theme.breakpoints.up('md')]: { right: '15px' },
            })}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      )}
      <DialogContent onClick={onModalContentClick} onFocus={onModalContentClick} sx={{ p: 0, overflowY: 'visible' }}>
        {children}
      </DialogContent>
      {buttons && (
        <DialogActions sx={{ p: '26px 0 0', '& .MuiButtonBase-root': { fontSize: '14px' } }}>
          {buttons}
          {hasCancelButton ? (
            <Button
              data-testid="close-modal-button"
              key="cancelBtn"
              color="primary"
              variant="outlined"
              onClick={onToggle}
            >
              Cancel
            </Button>
          ) : null}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
